import {
  SegmentedFilter,
  SegmentedFilterOption,
} from "@/components/common/segmented-filter/segmented-filter";
import { IssueStatusPill } from "@/components/issues/issue-status-pill/issue-status-pill";
import { cn } from "@/lib/utils";
import type { IssueStatus, IssueSummary } from "@/types/api";

/** `null` is the All option — no status filter at all. */
export type StatusSelection = IssueStatus | null;

const ORDER: IssueStatus[] = [
  "assigned",
  "in_progress",
  "completed",
  "closed",
];

interface IssueStatusNavProps {
  value: StatusSelection;
  onChange: (value: StatusSelection) => void;
  /** Undefined while the counts are loading. */
  summary: IssueSummary | undefined;
  /** Total across every status, for the All option. */
  totalCount: number | undefined;
  /**
   * Replaces the row's default `px-4 pb-2.5`, for a host that sets its own
   * inset — an issues report sits it in a column that already has one.
   */
  className?: string;
}

/**
 * Status navigation for the issues list.
 *
 * Status is navigation, not a filter: it stays on screen with its counts,
 * because those counts are the reason to look at the page — you need to see
 * what is sitting in the tabs you are *not* looking at. Everything else lives
 * behind the filter drawer. See [[issues-page.spec]].
 *
 * Options never disappear when their count is zero. A vanishing tab would make
 * the row reflow as data changes, and "nothing is overdue" is information worth
 * showing rather than hiding.
 */
export function IssueStatusNav({
  value,
  onChange,
  summary,
  totalCount,
  className = "px-4 pb-2.5",
}: IssueStatusNavProps) {
  return (
    <div
      className={cn(
        "overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      <SegmentedFilter aria-label="Filter issues by status" className="w-max">
        <StatusOption
          selected={value === null}
          onSelect={() => onChange(null)}
          label="All"
          count={totalCount}
        />
        {ORDER.map((status) => (
          <StatusOption
            key={status}
            selected={value === status}
            onSelect={() => onChange(status)}
            status={status}
            count={summary?.[status]}
          />
        ))}
      </SegmentedFilter>
    </div>
  );
}

interface StatusOptionProps {
  selected: boolean;
  onSelect: () => void;
  status?: IssueStatus;
  label?: string;
  count: number | undefined;
}

function StatusOption({
  selected,
  onSelect,
  status,
  label,
  count,
}: StatusOptionProps) {
  return (
    <SegmentedFilterOption selected={selected} onSelect={onSelect}>
      {status ? (
        <IssueStatusPill status={status} variant="dot" />
      ) : (
        <span>{label}</span>
      )}
      {/*
        The count is omitted, not zeroed, while it loads. Rendering a 0 that
        then becomes 35 is a lie the user may act on; an absent number reads
        as "not known yet". The row keeps its height either way because the
        label carries it.
      */}
      {count != null && (
        // Quiet on every segment but the chosen one, whose count is mint in
        // dark mode so the selected tab reads at a glance. See
        // [[segmented-filter.spec]].
        <span
          className={cn("opacity-60", selected && "dark:text-primary dark:opacity-100")}
        >
          {count}
        </span>
      )}
    </SegmentedFilterOption>
  );
}
